import React from 'react';
import { FocusPanelLoaderLayer } from '../../../../../../../components/general/loading-state/loader-layers/focus-panel-loader-layer/focus-panel-loader-layer';
import { OrganizationLevelType } from '../../../../../../../enums';
import { PermissionType } from '../../../../../../../enums/permission-type';
import { extractHighestOrganizationLevel } from '../../../../../../../library/helpers/permissions/extract-highest-organization-level';
import { IManagePersonPermissionsFocusPanelProps, ManagePersonPermissionsCascadingPanelIds } from '../../manage-person-panels.models';
import { PagePath } from '../../../../../../../navigation/navigation.enums';
import { Anchor, AvatarIcon, ChevronIcon, Chip, ListItemLayout, ListLayout, useMatchScreenWidth } from '@keplerco/core';
import { extractPermission } from '../../../../../../../library/helpers/permissions/extract-permission';
import managePersonPanelsStyles from '../../manage-person-panels.module.css';

export function ManagePersonPermissionsFocusPanel(props: IManagePersonPermissionsFocusPanelProps): JSX.Element {
  const isMobile = useMatchScreenWidth('mobile');

  function generateChip(permissionType: PermissionType): JSX.Element {
    const permission = extractPermission(props.permissions, permissionType);
    if (!permission) return <Chip customTheme={{ backgroundColor: 'background', borderColor: 'borders', color: 'text' }}>No permissions assigned</Chip>;

    const organizationLevel = extractHighestOrganizationLevel(permission.organizationLevels);

    switch (organizationLevel?.organizationLevel) {
      case OrganizationLevelType.Company: {
        return <Chip themeColour="g">Whole company</Chip>;
      }

      case OrganizationLevelType.Department: {
        return <Chip themeColour="g">{organizationLevel.entities.length > 1 ? 'Multiple departments' : 'Department only'}</Chip>;
      }

      case OrganizationLevelType.Team: {
        return <Chip themeColour="g">{organizationLevel.entities.length > 1 ? 'Multiple teams' : 'Team only'}</Chip>;
      }

      default: {
        return <Chip customTheme={{ backgroundColor: 'background', borderColor: 'borders', color: 'text' }}>No access</Chip>;
      }
    }
  }

  return (
    <FocusPanelLoaderLayer path={PagePath.userManagementPeople}>
      <div className="panelContent">
        <header className={managePersonPanelsStyles.header}>
          <div className={managePersonPanelsStyles.avatar}>
            <AvatarIcon name={props.person} />
          </div>

          <div>
            <h3>Permissions</h3>

            {!!props.person && (
              <span className="body">
                <strong>Name</strong> {props.person.firstName} {props.person.lastName} | <strong>Department</strong> {props.person.departmentName} | <strong>Team</strong> {props.person.teamName}
              </span>
            )}
          </div>
        </header>

        <ListLayout>
          <ListItemLayout onClick={() => props.onClickSection(ManagePersonPermissionsCascadingPanelIds.UserManagement)}>
            <div className="card" style={{ display: 'grid', gridTemplateColumns: `1fr ${!isMobile ? 'auto' : ''} auto`, alignItems: 'center', gap: 15 }}>
              User Management
              {!isMobile && generateChip(PermissionType.Administration)}
              <ChevronIcon tone="primary" />
            </div>
          </ListItemLayout>

          <ListItemLayout onClick={() => props.onClickSection(ManagePersonPermissionsCascadingPanelIds.Analytics)}>
            <div className="card" style={{ display: 'grid', gridTemplateColumns: `1fr ${!isMobile ? 'auto' : ''} auto`, alignItems: 'center', gap: 15 }}>
              Analytics
              {!isMobile && generateChip(PermissionType.Analytics)}
              <ChevronIcon tone="primary" />
            </div>
          </ListItemLayout>

          <ListItemLayout onClick={() => props.onClickSection(ManagePersonPermissionsCascadingPanelIds.AssessmentManagement)}>
            <div className="card" style={{ display: 'grid', gridTemplateColumns: `1fr ${!isMobile ? 'auto' : ''} auto`, alignItems: 'center', gap: 15 }}>
              Assessment management
              {!isMobile && generateChip(PermissionType.Assessments)}
              <ChevronIcon tone="primary" />
            </div>
          </ListItemLayout>

          <ListItemLayout onClick={() => props.onClickSection(ManagePersonPermissionsCascadingPanelIds.LearningManagement)}>
            <div className="card" style={{ display: 'grid', gridTemplateColumns: `1fr ${!isMobile ? 'auto' : ''} auto`, alignItems: 'center', gap: 15 }}>
              Learning Management
              {!isMobile && generateChip(PermissionType.LearningManagement)}
              <ChevronIcon tone="primary" />
            </div>
          </ListItemLayout>

          <ListItemLayout onClick={() => props.onClickSection(ManagePersonPermissionsCascadingPanelIds.RoleSkillManagement)}>
            <div className="card" style={{ display: 'grid', gridTemplateColumns: `1fr ${!isMobile ? 'auto' : ''} auto`, alignItems: 'center', gap: 15 }}>
              Role & Skill Management
              {!isMobile && generateChip(PermissionType.RoleSkillManagement)}
              <ChevronIcon tone="primary" />
            </div>
          </ListItemLayout>
        </ListLayout>

        <footer className="panelFooter">
          <Anchor onClick={props.onCancel}>Cancel</Anchor>
        </footer>
      </div>
    </FocusPanelLoaderLayer>
  );
}
